/*
 * 维护单离线能力（与 wechat_web 中的同名文件保持一致）
 * 机房里经常没有信号，按操作性质分两类处理：
 *   1. 校验类（打卡、人脸识别、资质识别）：结果必须由服务器给出，没网时不能操作，只给网络提示
 *   2. 过程类（检查项、步骤开关、照片、表格、总结等）：没网时先暂存在本机，联网后按操作顺序自动补传
 * 本文件提供：
 *   MW.offline.box     操作队列的本机存储（localStorage，按 工单实例 + 处理人 区分）
 *   MW.files           没网时拍的照片暂存（IndexedDB），联网后再上传
 *   MW.offline.qr      扫码比对（规则同后端 SubTicketStatus.check_qr_code，前端即可得出结论，无需联网）
 *   MW.offline.threshold  结束步骤的阈值比较（规则同后端 _check_threshold，没网时先在本机出结果，联网后以服务器为准）
 */
export default function installOffline(MW) {
  var util = MW.util;
  var off = (MW.offline = {});

  /* ================= 操作队列的本机存储 ================= */
  var BOX_PREFIX = "mw_outbox:";
  off.box = {
    key: function () {
      var ctx = MW.store.get("$context") || {};
      if (!ctx.instanceId) return "";
      return BOX_PREFIX + ctx.instanceId + ":" + (ctx.user || "");
    },
    load: function () {
      var k = off.box.key();
      if (!k) return null;
      try {
        var raw = localStorage.getItem(k);
        var d = raw ? JSON.parse(raw) : null;
        return d && Array.isArray(d.list) ? d : null;
      } catch (e) {
        return null;
      }
    },
    save: function (list, extra) {
      var k = off.box.key();
      if (!k) return false;
      try {
        if (!list.length && !(extra && Object.keys(extra.done || {}).length)) localStorage.removeItem(k);
        else localStorage.setItem(k, JSON.stringify(Object.assign({ v: 1, at: util.now(), list: list }, extra || {})));
        return true;
      } catch (e) {
        console.warn("[mw] 本机暂存失败", e);
        return false;
      }
    },
    clear: function () {
      var k = off.box.key();
      if (k) try { localStorage.removeItem(k); } catch (e) {}
    },
  };

  /* ================= 照片暂存（IndexedDB） ================= */
  var DB = "mw_offline", STORE = "files";
  var dbp = null;
  function db() {
    if (dbp) return dbp;
    dbp = new Promise(function (resolve, reject) {
      if (!window.indexedDB) return reject(new Error("当前环境不支持本机暂存照片"));
      var req = indexedDB.open(DB, 1);
      req.onupgradeneeded = function () {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
      };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error || new Error("本机暂存不可用")); };
    });
    dbp.catch(function () { dbp = null; });
    return dbp;
  }
  function tx(mode, fn) {
    return db().then(function (d) {
      return new Promise(function (resolve, reject) {
        var t = d.transaction(STORE, mode);
        var st = t.objectStore(STORE);
        var r = fn(st);
        t.oncomplete = function () { resolve(r && "result" in r ? r.result : undefined); };
        t.onerror = function () { reject(t.error || new Error("本机暂存失败")); };
        t.onabort = function () { reject(t.error || new Error("本机空间不足，暂存失败")); };
      });
    });
  }
  var thumbs = {}; // localId -> objectURL（仅本次打开有效）
  var loading = {};
  MW.files = {
    /** 暂存一个文件，返回 localId */
    put: function (blob, name) {
      var id = "f" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      return tx("readwrite", function (st) { return st.put({ blob: blob, name: name || "photo.jpg", type: blob.type || "", at: Date.now() }, id); }).then(function () {
        try { thumbs[id] = URL.createObjectURL(blob); } catch (e) {}
        return id;
      });
    },
    get: function (id) {
      return tx("readonly", function (st) { return st.get(id); });
    },
    del: function (id) {
      if (thumbs[id]) try { URL.revokeObjectURL(thumbs[id]); } catch (e) {}
      delete thumbs[id];
      return tx("readwrite", function (st) { return st.delete(id); }).catch(function () {});
    },
    /** 待上传照片的预览地址；首次调用时异步读取，读到后回调 onReady */
    thumb: function (id, onReady) {
      if (thumbs[id]) return thumbs[id];
      if (!loading[id]) {
        loading[id] = MW.files.get(id).then(function (rec) {
          if (rec && rec.blob) thumbs[id] = URL.createObjectURL(rec.blob);
          return thumbs[id];
        }, function () { return ""; });
      }
      if (onReady) loading[id].then(function (u) { if (u) onReady(u); });
      return "";
    },
  };
  /** 文件项是否为"待上传"（没网时拍的照片） */
  util.isPendingFile = function (f) {
    return !!(f && typeof f === "object" && f._local);
  };

  /* ================= 扫码比对（与后端 check_qr_code 一致） ================= */
  var SCAN_OK = "匹配成功,该设备编号为";
  off.qr = function (raw, deviceNumber) {
    var number = "";
    if (raw && typeof raw === "object") number = raw.DevC || "";
    else {
      var s = String(raw == null ? "" : raw).trim();
      if (s.charAt(0) === "{" && s.charAt(s.length - 1) === "}") {
        var m = s.match(/["']DevC["']\s*:\s*["']([^"']*)["']/);
        number = m ? m[1] : "";
      } else number = s;
    }
    var dn = deviceNumber == null ? "" : String(deviceNumber);
    var ok = number === dn;
    return { number: number, ok: ok, result: ok ? SCAN_OK + dn : MW.biz.SCAN_FAIL };
  };

  /* ================= 阈值比较（与后端 _check_threshold 一致，s_r1 由服务器补齐） ================= */
  off.threshold = function (subs) {
    subs = subs || [];
    var hasPoint = subs.some(function (s) { return s && (s.sub_measure_point_list || []).length; });
    if (!hasPoint) return subs;
    subs.forEach(function (sub) {
      if (!sub || (sub.r2 || "") === "不适用") return;
      var pts = sub.sub_measure_point_list || [];
      if (!pts.length) return;
      var n1 = parseFloat(sub.n1);
      if (isNaN(n1)) n1 = 0;
      var text = "", status = "正常";
      pts.forEach(function (p) {
        var name = p.measure_point_name || "";
        if (p.max_value != null && p.min_value != null) {
          if (n1 > parseFloat(p.max_value) || n1 < parseFloat(p.min_value)) {
            text = ["超出阈值范围"];
            status = "异常";
          } else text = ["【" + name + "】当前为" + n1 + ",在阈值范围内"];
        } else text = ["【" + name + "】当前为" + n1 + ",无阈值可比较"];
      });
      var abn = status === "异常";
      sub.z2 = text;
      sub.r1 = sub.r2 = abn ? "异常" : "正常";
      sub.i1 = abn ? text : [];
      sub.a1 = abn ? ["数值异常"] : [];
    });
    return subs;
  };
  /** 阈值比较会改动的检查项字段 */
  off.THRESHOLD_FIELDS = ["z2", "r1", "r2", "i1", "a1"];

  /* ================= 网络提示 ================= */
  /** 校验类操作的统一网络提示（不弹红色报错） */
  off.needNet = function (what) {
    MW.ui.toast("当前网络不可用，" + what + "需要联网完成，请到有信号的位置再操作", "warn");
  };
}
